import { useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import { IAlarm } from '_/api/alarms/alarms.api';
import AsyncPageContainer from '_/common/components/layout/AsyncPageContainer';
import RunbookTextEditor from '_/common/components/RunbookTextEditor';
import { useAlarmGroupContext } from '_/contexts/AlarmGroupContext';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useAlarms, useUpdateAlarmReadme } from '_/hooks/useAlarms';
import useOrgAccess from '_/hooks/useOrgAccess';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import React, { Fragment, ReactElement, useEffect } from 'react';

function AlarmGroupRunbook(): ReactElement {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { selectedAlarmGroup, alarmGroupId } = useAlarmGroupContext();
  const params = useParams({ strict: false }) as Partial<{
    orgSlug: string;
    projectSlug: string;
    flowSlug: string;
  }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const flowSlug = params.flowSlug || '';
  const { selectedFlow } = useFlowContext();
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const { setBreadcrumbs } = useTopHeader();

  // Fetch alarms for this alarm group
  const { data: alarmsData = [], isLoading: isFetchingAlarms } = useAlarms(alarmGroupId);
  const runbookAlarms = alarmsData as IAlarm[];

  // We need to create a mutation hook that doesn't need alarmId upfront
  // since we'll be calling it with different alarmIds
  const updateReadmeMutation = useUpdateAlarmReadme(undefined, alarmGroupId);

  const handleSaveReadme = async (alarmId: string, readme: string) => {
    // Update the mutation to use the specific alarmId
    await updateReadmeMutation.mutateAsync({ alarmId: alarmId, readme: readme });
  };

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/home`,
      },
      {
        title: 'Alarm Groups',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups`,
      },
      {
        title: selectedAlarmGroup?.name || 'Alarm Group',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups/${alarmGroupId}/home`,
      },
      { title: 'Runbook' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization?.name,
    orgSlug,
    selectedProject?.name,
    projectSlug,
    selectedFlow?.name,
    flowSlug,
    selectedAlarmGroup?.name,
    alarmGroupId,
  ]);

  return (
    <AsyncPageContainer isLoading={isFetchingAlarms}>
      <PageContent>
        {runbookAlarms.length ? (
          <Fragment>
            {runbookAlarms.map((alarm) => (
              <RunbookTextEditor
                key={alarm.id}
                id={alarm.id}
                name={alarm.name}
                onSaveClick={handleSaveReadme}
                access={access}
                href={`/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups/${selectedAlarmGroup?.id}/alarms/${alarm.id}`}
                text={alarm.readme?.content || ''}
              />
            ))}
          </Fragment>
        ) : (
          <div>No Runbook Data to Show</div>
        )}
      </PageContent>
    </AsyncPageContainer>
  );
}

export default AlarmGroupRunbook;
